// src/components/Teacher/TeacherAssignments.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    collection, query, where, getDocs, doc,
    updateDoc, deleteDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import CreateAssignmentModal from './CreateAssignmentModal';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface Assignment {
    id: string;
    title: string;
    description: string;
    classId: string;
    className: string;
    dueDate: Timestamp;
    createdAt: Timestamp;
    status: 'active' | 'archived';
    submissionCount: number;
    minWords?: number;
    maxWords?: number;
    prompt?: string;
}
 
interface ClassOption {
    id: string;
    name: string;
}
 
const getAssignmentStatus = (a: Assignment): 'active' | 'past-due' | 'archived' => {
    if (a.status === 'archived') return 'archived';
    const due = a.dueDate?.toDate?.();
    if (due && due < new Date()) return 'past-due';
    return 'active';
};
 
const statusBadge = (status: 'active' | 'past-due' | 'archived') => {
    const map = {
        'active':   { label: 'Active',    bg: C.secondaryContainer, color: C.secondary },
        'past-due': { label: 'Past Due',  bg: C.errorContainer,     color: C.error },
        'archived': { label: 'Archived',  bg: C.surfaceHighest,     color: C.onSurfaceVariant },
    };
    const s = map[status];
    return (
        <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            {s.label}
        </span>
    );
};
 
const formatDate = (ts: Timestamp) => {
    if (!ts?.toDate) return '—';
    return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
 
// ── Main component ─────────────────────────────────────────────────────────────
const TeacherAssignments: React.FC = () => {
    const navigate   = useNavigate();
    const { currentUser } = useAuth();
 
    const [assignments, setAssignments]   = useState<Assignment[]>([]);
    const [classes, setClasses]           = useState<ClassOption[]>([]);
    const [loading, setLoading]           = useState(true);
    const [filterClass, setFilterClass]   = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [showModal, setShowModal]       = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    const load = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            // Load assignments
            const aSnap = await getDocs(
                query(collection(db, 'assignments'), where('teacherId', '==', currentUser.uid))
            );
            const loaded: Assignment[] = aSnap.docs.map(d => ({ id: d.id, ...d.data() } as Assignment));
    
            // Load classes and build student map
            const cSnap = await getDocs(
                query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid))
            );
            setClasses(cSnap.docs.map(d => ({ id: d.id, name: (d.data() as any).name })));
            const classStudents: Record<string, string[]> = {};
            cSnap.docs.forEach(d => {
                classStudents[d.id] = Object.keys(d.data().students || {});
            });
    
            // Count submissions for each assignment
            const loadedWithCounts = await Promise.all(loaded.map(async (assignment) => {
                const studentIds = classStudents[assignment.classId] || [];
                let submissionCount = 0;
                for (const sid of studentIds) {
                    const statusSnap = await getDocs(query(
                        collection(db, 'studentAssignments'),
                        where('assignmentId', '==', assignment.id),
                        where('studentId', '==', sid),
                        where('status', 'in', ['submitted', 'returned'])
                    ));
                    if (!statusSnap.empty) submissionCount++;
                }
                return { ...assignment, submissionCount };
            }));
    
            loadedWithCounts.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
            setAssignments(loadedWithCounts);
        } catch (err) {
            console.error('Error loading assignments:', err);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);
 
    useEffect(() => { load(); }, [load]);
 
    const handleArchive = async (id: string, currentStatus: string) => {
        const newStatus = currentStatus === 'archived' ? 'active' : 'archived';
        await updateDoc(doc(db, 'assignments', id), { status: newStatus, updatedAt: Timestamp.now() });
        setAssignments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus as any } : a));
    };
 
    const handleDelete = async (id: string) => {
        await deleteDoc(doc(db, 'assignments', id));
        setAssignments(prev => prev.filter(a => a.id !== id));
        setConfirmDelete(null);
    };
 
    // Filtered list
    const filtered = assignments.filter(a => {
        const classMatch  = filterClass  === 'all' || a.classId === filterClass;
        const status      = getAssignmentStatus(a);
        const statusMatch = filterStatus === 'all' || status === filterStatus;
        return classMatch && statusMatch;
    });
 
    // Stats
    const activeCount   = assignments.filter(a => getAssignmentStatus(a) === 'active').length;
    const pastDueCount  = assignments.filter(a => getAssignmentStatus(a) === 'past-due').length;
    const archivedCount = assignments.filter(a => getAssignmentStatus(a) === 'archived').length;
 
    const pillBtn = (label: string, value: string, current: string, setter: (v: string) => void, accent: string) => (
        <button
            key={value}
            onClick={() => setter(value)}
            style={{
                padding: '8px 18px', borderRadius: '9999px', border: 'none',
                background: current === value ? accent : C.surfaceHigh,
                color: current === value ? '#fff' : C.onSurfaceVariant,
                fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer',
                fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s',
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </button>
    );
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginBottom: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={{ background: 'none', border: 'none', color: C.onSurfaceVariant, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: '8px', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            ← Dashboard
                        </button>
                        <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: '900', color: C.onBackground, margin: 0 }}>
                            Assignments
                        </h1>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '4px 0 0 0' }}>
                            {assignments.length} total across {classes.length} class{classes.length !== 1 ? 'es' : ''}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '12px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}40`, transition: 'opacity 0.15s, transform 0.15s', whiteSpace: 'nowrap' }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        ➕ New Assignment
                    </button>
                </div>
 
                {/* ── Stat pills ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '28px' }}>
                    <div style={{ background: C.secondaryContainer, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.secondary }}>{activeCount}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active</span>
                    </div>
                    <div style={{ background: C.errorContainer, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.error }}>{pastDueCount}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.error, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Past Due</span>
                    </div>
                    <div style={{ background: C.surfaceHighest, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onSurfaceVariant }}>{archivedCount}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Archived</span>
                    </div>
                </div>
 
                {/* ── Filters ─────────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
                    {/* Status filter */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {pillBtn('All', 'all', filterStatus, setFilterStatus, C.onSurface)}
                        {pillBtn('Active', 'active', filterStatus, setFilterStatus, C.secondary)}
                        {pillBtn('Past Due', 'past-due', filterStatus, setFilterStatus, C.error)}
                        {pillBtn('Archived', 'archived', filterStatus, setFilterStatus, C.onSurfaceVariant)}
                    </div>
 
                    {/* Class filter */}
                    {classes.length > 1 && (
                        <select
                            value={filterClass}
                            onChange={e => setFilterClass(e.target.value)}
                            style={{ padding: '8px 14px', borderRadius: '9999px', border: `1.5px solid ${C.outlineVariant}`, background: C.surfaceLowest, color: C.onSurface, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none' }}
                        >
                            <option value="all">All Classes</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                </div>
 
                {/* ── Assignment list ──────────────────────────────────────── */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: C.onSurfaceVariant, fontSize: '1rem' }}>
                        Loading assignments...
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '60px 32px', textAlign: 'center', border: `1px solid ${C.surfaceHigh}` }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>📋</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.3rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {assignments.length === 0 ? 'No assignments yet' : 'No assignments match these filters'}
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>
                            {assignments.length === 0 ? 'Create your first assignment to get students writing!' : 'Try adjusting the filters above.'}
                        </p>
                        {assignments.length === 0 && (
                            <button
                                onClick={() => setShowModal(true)}
                                style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '12px 28px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Create First Assignment
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filtered.map(a => {
                            const status = getAssignmentStatus(a);
                            const isArchived = status === 'archived';
                            return (
                                <div
                                    key={a.id}
                                    style={{ background: C.surfaceLowest, borderRadius: '18px', padding: '20px 24px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', opacity: isArchived ? 0.65 : 1, transition: 'box-shadow 0.2s' }}
                                    onMouseEnter={e => { if (!isArchived) (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                                >
                                    {/* Left: info */}
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, margin: 0 }}>
                                                {a.title}
                                            </h3>
                                            {statusBadge(status)}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <span>📚 {a.className}</span>
                                            <span>📅 Due {formatDate(a.dueDate)}</span>
                                            {(a.minWords || a.maxWords) && (
                                                <span>✍️ {a.minWords ? `${a.minWords}` : '0'}–{a.maxWords ? `${a.maxWords}` : '∞'} words</span>
                                            )}
                                        </div>
                                        {a.description && (
                                            <p style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, margin: '8px 0 0 0', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {a.description}
                                            </p>
                                        )}
                                    </div>
 
                                    {/* Middle: submission count */}
                                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.primary, lineHeight: 1 }}>
                                            {a.submissionCount ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.65rem', fontWeight: '700', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
                                            Submissions
                                        </div>
                                    </div>
 
                                    {/* Right: actions */}
                                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                        <button
                                            onClick={() => handleArchive(a.id, a.status)}
                                            title={isArchived ? 'Unarchive' : 'Archive'}
                                            style={{ background: C.surfaceLow, color: C.onSurfaceVariant, border: 'none', borderRadius: '9999px', padding: '8px 14px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                            onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHigh; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLow; }}
                                        >
                                            {isArchived ? '📤 Restore' : '📥 Archive'}
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(a.id)}
                                            title="Delete assignment"
                                            style={{ background: C.errorContainer, color: C.error, border: 'none', borderRadius: '9999px', padding: '8px 14px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                            onMouseEnter={e => { e.currentTarget.style.background = C.error; e.currentTarget.style.color = '#fff'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = C.errorContainer; e.currentTarget.style.color = C.error; }}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
 
            {/* ── Delete confirmation modal ────────────────────────────────── */}
            {confirmDelete && (
                <div
                    onClick={() => setConfirmDelete(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '32px', maxWidth: '400px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                        <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>🗑️</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            Delete this assignment?
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 24px 0', lineHeight: '1.6' }}>
                            This will permanently delete the assignment and all associated data. This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setConfirmDelete(null)}
                                style={{ flex: 1, padding: '12px', background: C.surfaceLowest, color: C.onSurfaceVariant, border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(confirmDelete)}
                                style={{ flex: 1, padding: '12px', background: C.error, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
 
            {/* ── Create assignment modal ──────────────────────────────────── */}
            <CreateAssignmentModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                classId=""
                className=""
                onAssignmentCreated={() => { setShowModal(false); load(); }}
            />
        </div>
    );
};
 
export default TeacherAssignments;